import { useEffect, useState } from 'react'
import { api, type ReviewItem } from '../api'
import { useDesks } from '../desks'

/**
 * The review queue — TECHNICAL_DESIGN.md §9.2, §9.3.
 *
 * §9 exists because of proposal §2.1: the spreadsheet was not wrong because it
 * was a spreadsheet, it was wrong because nothing forced anyone to confirm it was
 * still current. This screen is what does the forcing.
 *
 * §9.3 names three structural controls against the rubber stamp, and two of them
 * are this file's shape rather than its copy:
 *
 *   * **The source clause is shown beside the rule**, with the document and the
 *     version. Confirming is meant to require looking at the document, and a row
 *     showing only the threshold asks *does this number look plausible* — which
 *     is the question everybody answers yes to.
 *
 *   * **There is no bulk control.** No select-all, no multi-select, one button
 *     per rule. A "confirm all" would turn the feature into theatre while making
 *     the metrics look excellent, which is the worst available outcome. The API
 *     will not accept a list either; this is the same refusal at the other end.
 *
 * The third — staggering review dates — is not implemented: the store accepts
 * a date and nothing passes one, so a book approved in one sitting comes due in
 * one day. Known, and left; see IMPLEMENTATION.md.
 *
 * A rule whose document does not resolve is shown with the confirm button
 * **removed rather than disabled**, and the reason stated. It can be amended or
 * flagged, not confirmed (§9.3), and a greyed-out button invites the reader to
 * hunt for the permission they are missing rather than read why.
 *
 * **Mine and Everyone are two entries in the menu, not two tabs on one screen.**
 * They are the difference between *my work* and *the firm's backlog*, which is
 * the difference between a queue somebody opens every morning and a report
 * somebody opens once a month. A segmented control inside the screen made the
 * second one reachable only by first opening the first, and made neither
 * something you could navigate to.
 */
export function Reviews({ mine = true }: { mine?: boolean }) {
  const [items, setItems] = useState<ReviewItem[]>([])
  const [ahead, setAhead] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<Record<string, string>>({})
  /** The desk the reviews are narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const desks = useDesks()
  const deskOf = (item: ReviewItem) =>
    (item.portfolio_id ? desks.fund(item.portfolio_id) : desks.group(item.scope_id)) ?? ''
  const listed = items.filter((item) => !desk || deskOf(item) === desk)

  async function load() {
    setError(null)
    try {
      setItems(await api.reviews(mine, ahead))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [mine, ahead])

  async function confirm(item: ReviewItem) {
    if (!item.source_document_id) return
    try {
      // The document version is named by the reviewer, from the row they were
      // shown. The store knows which one the rule cites; the point is that a
      // confirmation which could be sent without the document in front of you is
      // one that will be (§9.2).
      const result = await api.confirmReview(item.rule_id, item.source_document_id)
      setDone({ ...done, [item.rule_id]: result.next_review_due_on })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="reviews">
      <header className="page-head">
        <h2>{mine ? 'Your rules due for review' : 'Every rule due for review'}</h2>
        <p className="note">
          {mine
            ? 'Rules you own whose review date has come round, and documents that ' +
              'have changed since a rule cited them. Confirming records that ' +
              'somebody checked; it does not change the rule.'
            : 'The firm’s whole backlog, whoever owns it — rules whose review date ' +
              'has come round, and documents that have changed since a rule cited ' +
              'them. Confirming records that somebody checked; it does not change ' +
              'the rule.'}
        </p>
      </header>

      <div className="filters">
        <label className="ahead">
          Look ahead (days)
          <input
            type="number"
            min={0}
            max={365}
            value={ahead}
            onChange={(e) => setAhead(Number(e.target.value))}
          />
        </label>
        {desks.firmWide && desks.desks.length > 0 && (
          <label>
            Desk
            <select value={desk} onChange={(e) => setDesk(e.target.value)} data-testid="reviews-desk">
              <option value="">Every desk</option>
              {desks.desks.map((d) => (
                <option key={d.id} value={d.code}>
                  {d.code} · {d.name}
                </option>
              ))}
              <option value="Whole firm">Whole firm’s groups</option>
            </select>
          </label>
        )}
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {listed.length === 0 && <p className="empty">Nothing is due.</p>}

      {listed.map((item) => (
        <section className="review" key={item.rule_id} data-testid="review">
          <h3>
            <code>{item.rule_key}</code>
            <span className="limit">
              {item.operator ?? ''} {String(item.threshold ?? '—')}
            </span>
            {item.days_overdue >= 0 && (
              <span className="overdue" data-testid="overdue">
                {item.days_overdue} day(s) overdue
              </span>
            )}
          </h3>

          {/* §9.3's first control: the clause travels with the row. */}
          <p className="provenance" data-testid="provenance">
            {item.source_document_title ?? 'no document'}
            {item.source_version_label && <span> ({item.source_version_label})</span>}
            {item.source_clause && <span className="clause"> — {item.source_clause}</span>}
          </p>

          {item.drift_kind && (
            <p className="warn" data-testid="drift">
              The governing document has changed since this rule was recorded
              ({item.drift_kind}). Read it before confirming.
            </p>
          )}

          {item.scope_id && (
            <p className="note" data-testid="reach">
              Firm policy on <b>{item.scope_code}</b>, reaching{' '}
              {item.affected_portfolio_count} portfolio(s). Reviewed once, here —
              not by each portfolio owner.
            </p>
          )}

          {done[item.rule_id] && (
            <p className="done">Confirmed. Next review {done[item.rule_id]}.</p>
          )}

          {item.document_status === 'unresolved' || !item.source_document_id ? (
            <p className="warn" data-testid="unconfirmable">
              This rule's source cannot be retrieved, so it cannot be confirmed.
              Amend it against whatever the document now says, or leave the
              finding open.
            </p>
          ) : (
            /* One button, one rule. There is deliberately no select-all. */
            <button onClick={() => void confirm(item)}>Confirm unchanged</button>
          )}
        </section>
      ))}
    </div>
  )
}
